/**
 * ScheduleDraft timeline adapter.
 * It accepts neutral track/item view-models and projects them to vis-timeline.
 * Design docs -> frontend/src/features/flightSchedule/ScheduleDraft/docs/20_workspace_ux/20_multitrack_view.md
 */

import { Timeline } from "vis-timeline/esnext";
import { DataSet } from "vis-data/esnext";
import moment from "moment";
import xss from "xss";
import { debugScheduleDraftTimeline, isScheduleDraftTimelineDebugEnabled } from "../lib/timelineDebug";

const DEFAULT_ITEM_DURATION_MS = 30 * 60 * 1000;
const TIMELINE_ITEM_MARGIN = { horizontal: 6, vertical: 6 };
const TIMELINE_AXIS_MARGIN = 10;
const TIMELINE_MIN_HEIGHT = 84;
const TIMELINE_AXIS_HEIGHT = 44;
const TIMELINE_TRACK_ROW_HEIGHT = 64;
const ITEM_PREVIEW_TRIGGER_CLICK = "click";
const ITEM_PREVIEW_TRIGGER_HOVER = "hover";
const ITEM_PREVIEW_TRIGGER_NONE = "none";
const FLIGHT_TITLE_DEFAULT_FONT_SIZE = 11;
const FLIGHT_TITLE_MIN_FONT_SIZE = 8;
const FLIGHT_TIME_DEFAULT_FONT_SIZE = 8;
const FLIGHT_TIME_MIN_FONT_SIZE = 6;
const FLIGHT_TITLE_CHAR_WIDTH = 6.4;
const FLIGHT_TIME_CHAR_WIDTH = 4.2;
const FLIGHT_TITLE_HORIZONTAL_PADDING = 4;
const FLIGHT_TITLE_CENTER_PADDING = 6;
const FLIGHT_MIN_CENTER_RESERVE_WIDTH = 30;
const FLIGHT_TIME_CENTER_GAP = 4;
const DEFAULT_TIMELINE_XSS_WHITE_LIST = xss.getDefaultWhiteList();
const AIRCRAFT_AVAILABILITY_ITEM_TYPE = "aircraft-availability";

function extendTimelineXssAttrs(tag, attrs) {
    return Array.from(new Set([...(DEFAULT_TIMELINE_XSS_WHITE_LIST[tag] || []), ...attrs]));
}

// vis-timeline filters string content before insertion; these attrs are the DOM
// contract for responsive item layout.
const TIMELINE_CONTENT_XSS_FILTER_OPTIONS = {
    whiteList: {
        ...DEFAULT_TIMELINE_XSS_WHITE_LIST,
        div: extendTimelineXssAttrs("div", [
            "class",
            "data-timeline-item-id",
            "data-timeline-lane",
            "data-flight-title",
            "data-flight-departure",
            "data-flight-arrival",
            "data-ground-role",
            "data-ground-time-status",
            "data-curfew-operation",
            "data-curfew-edge",
            "data-aircraft-reg",
            "data-availability-type",
            "data-availability-open-ended",
        ]),
        span: extendTimelineXssAttrs("span", ["class", "title", "aria-hidden"]),
        button: extendTimelineXssAttrs("button", [
            "class",
            "type",
            "data-timeline-track-action",
            "data-timeline-track-id",
            "aria-disabled",
            "aria-label",
        ]),
    },
};
const GROUND_MARKER_DISPLAY_MODES = ["horizontal", "vertical", "compact", "tooltip"];
const TIMELINE_LANES = {
    flight: {
        id: "flight",
        order: 0,
        subgroup: "flight",
        className: "schedule-draft-timeline-lane-flight",
    },
    ground: {
        id: "ground",
        order: 1,
        subgroup: "ground",
        className: "schedule-draft-timeline-lane-ground",
    },
    availability: {
        id: AIRCRAFT_AVAILABILITY_ITEM_TYPE,
        order: 0,
        subgroup: "context",
        className: "schedule-draft-timeline-lane-context",
    },
};

function escapeHtml(value) {
    return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function normalizeGroundDisplayMode() {
    return "horizontal";
}

function normalizeGroundMarkerRole(ground = {}) {
    if (ground.markerRole === "turnaround" || ground.markerRole === "discontinuity") {
        return ground.markerRole;
    }

    return ground.isContinuous ? "turnaround" : "discontinuity";
}

function sanitizeTimelineClassName(className, { isGroundItem = false } = {}) {
    return String(className || "")
        .split(/\s+/)
        .filter(Boolean)
        .filter((token) => !token.startsWith("schedule-draft-timeline-item-rotation-"))
        .filter((token) => !isGroundItem || !token.startsWith("schedule-draft-timeline-ground-mode-"))
        .join(" ");
}

function applyGroundMarkerDisplayMode(element, mode, { displayLabel = "", markerRole = "turnaround" } = {}) {
    const labelElement = element.querySelector?.(".schedule-draft-ground-display-label");
    const markerElement = element.querySelector?.(".schedule-draft-ground-compact-marker");

    GROUND_MARKER_DISPLAY_MODES.forEach((candidate) => {
        element.classList.toggle(`schedule-draft-timeline-ground-mode-${candidate}`, candidate === mode);
    });
    element.dataset.groundDisplayMode = mode;
    element.setAttribute("data-ground-display-mode", mode);

    if (labelElement) {
        labelElement.textContent = mode === "horizontal" || mode === "vertical" ? displayLabel : "";
    }
    if (markerElement) {
        markerElement.textContent = markerRole === "discontinuity" && mode === "compact" ? "!" : "";
    }
}

function renderTrackLabel(track) {
    const action = track.action;
    const actionTitle = action?.disabledReason || action?.label || "轨道操作";
    const actionButton = action
        ? `
            <span class="schedule-draft-track-action-wrap" title="${escapeHtml(actionTitle)}">
                <button
                    type="button"
                    class="schedule-draft-track-action-button ${action.disabled ? "schedule-draft-track-action-button-disabled" : ""}"
                    data-timeline-track-action="${escapeHtml(action.type)}"
                    data-timeline-track-id="${escapeHtml(action.trackId)}"
                    ${action.disabled ? "aria-disabled=\"true\"" : ""}
                    aria-label="${escapeHtml(`${action.label} ${track.label || "轨道"}`)}"
                >${escapeHtml(action.label)}</button>
            </span>
        `
        : "";
    return `
        <div class="schedule-draft-track-label">
            <span class="schedule-draft-track-title">${escapeHtml(track.label)}</span>
            ${actionButton}
        </div>
    `;
}

function isContextTimelineGroup(track) {
    return track?.kind === "context" || track?.role === "context";
}

function isReferenceTimelineGroup(track) {
    return !isContextTimelineGroup(track) && (track?.kind === "reference" || track?.role === "reference");
}

function isConnectorTimelineGroup(track) {
    return !isContextTimelineGroup(track) && (track?.kind === "compare-corridor" || track?.role === "connector");
}

function isEditingTimelineGroup(track) {
    return !isContextTimelineGroup(track) && (track?.kind === "editable" || track?.kind === "editing" || track?.role === "editing");
}

function orderTimelineGroups(tracks = [], contextLanes = []) {
    const groups = [...(contextLanes || []), ...(tracks || [])].filter(Boolean);
    return [
        ...groups.filter(isContextTimelineGroup),
        ...groups.filter(isReferenceTimelineGroup),
        ...groups.filter(isConnectorTimelineGroup),
        ...groups.filter(isEditingTimelineGroup),
        ...groups.filter((track) => !isContextTimelineGroup(track) && !isReferenceTimelineGroup(track) && !isConnectorTimelineGroup(track) && !isEditingTimelineGroup(track)),
    ];
}

function renderFlightItemContent(item) {
    const departureLabel = item.departureLabel || item.subtitle || "";
    const arrivalLabel = item.arrivalLabel || "";
    return `
        <div class="schedule-draft-item-content schedule-draft-flight-lane-content" data-timeline-item-id="${escapeHtml(item.id)}" data-timeline-lane="flight" data-flight-title="${escapeHtml(item.title)}" data-flight-departure="${escapeHtml(departureLabel)}" data-flight-arrival="${escapeHtml(arrivalLabel)}">
            <span class="schedule-draft-flight-time schedule-draft-flight-time-departure schedule-draft-item-subtitle">${escapeHtml(departureLabel)}</span>
            <span class="schedule-draft-item-title schedule-draft-flight-number schedule-draft-flight-number-center-anchor">${escapeHtml(item.title)}</span>
            <span class="schedule-draft-flight-time schedule-draft-flight-time-arrival schedule-draft-item-subtitle">${escapeHtml(arrivalLabel)}</span>
        </div>
    `;
}

function renderGroundItemContent(item) {
    const ground = item.meta?.ground || {};
    const displayMode = normalizeGroundDisplayMode(ground.displayMode, ground);
    const markerRole = normalizeGroundMarkerRole(ground);
    const displayLabel = ground.displayLabel || ground.airportCode || (ground.isContinuous ? "" : `${ground.arrivalAirport || "--"}->${ground.departureAirport || "--"}`);
    const compactLabel = ground.compactLabel ?? (ground.isContinuous ? "" : "!");
    const shouldRenderTextLabel = displayMode === "horizontal" || displayMode === "vertical";
    const markerLabel = markerRole === "discontinuity" && displayMode === "compact" ? compactLabel : "";

    return `
        <div class="schedule-draft-ground-marker-content schedule-draft-ground-lane-content schedule-draft-ground-content-${escapeHtml(displayMode)} schedule-draft-ground-role-${escapeHtml(markerRole)}" data-timeline-lane="ground" data-ground-role="${escapeHtml(markerRole)}">
            <span class="schedule-draft-ground-airport-code schedule-draft-ground-display-label">${shouldRenderTextLabel ? escapeHtml(displayLabel) : ""}</span>
            <span class="schedule-draft-ground-compact-marker">${escapeHtml(markerLabel)}</span>
        </div>
    `;
}

function renderGroundSpanContent(item) {
    const ground = item?.meta?.ground || {};
    const groundTimeStatus = ground.groundTimeStatus || "";
    return `
        <div class="schedule-draft-ground-span-content schedule-draft-ground-lane-content" data-timeline-lane="ground" data-ground-time-status="${escapeHtml(groundTimeStatus)}">
            <span class="schedule-draft-ground-rule" aria-hidden="true"></span>
        </div>
    `;
}

function renderCurfewBackgroundContent() {
    return `
        <div class="schedule-draft-curfew-band" aria-hidden="true"></div>
    `;
}

function renderCurfewLabelContent(item) {
    const curfew = item.meta?.curfew || {};
    const displayLabel = curfew.displayLabel || "起飞宵禁";
    const operation = curfew.tone || curfew.operation || "departure";

    return `
        <div class="schedule-draft-curfew-label-content schedule-draft-curfew-label-content-${escapeHtml(operation)}" data-curfew-operation="${escapeHtml(operation)}">
            <span class="schedule-draft-curfew-center-label">
                <span class="schedule-draft-curfew-label-text">${escapeHtml(displayLabel)}</span>
            </span>
        </div>
    `;
}

function renderCurfewEdgeTimeContent(item, edge) {
    const curfew = item.meta?.curfew || {};
    const operation = curfew.tone || curfew.operation || "departure";
    const label = edge === "start"
        ? curfew.edgeStartLabel || curfew.windowStartLabel || ""
        : curfew.edgeEndLabel || curfew.windowEndLabel || "";

    return `
        <div class="schedule-draft-curfew-edge-time-content schedule-draft-curfew-edge-time-content-${escapeHtml(operation)} schedule-draft-curfew-edge-time-content-${escapeHtml(edge)}" data-curfew-operation="${escapeHtml(operation)}" data-curfew-edge="${escapeHtml(edge)}">
            <span class="schedule-draft-curfew-edge-time">${escapeHtml(label)}</span>
        </div>
    `;
}

function renderAircraftAvailabilityItemContent(item) {
    const availabilityWindow = item.meta?.availabilityWindow || {};
    const aircraftReg = item.meta?.aircraftReg || availabilityWindow.aircraft_reg || "";
    const typeLabel = item.meta?.windowTypeLabel || availabilityWindow.window_type || "不可用";
    const title = item.meta?.fullDisplayLabel || item.fullDisplayLabel || item.title || typeLabel;
    const openEnded = item.meta?.openEnded ? "true" : "false";

    return `
        <div class="schedule-draft-availability-lane-content" data-timeline-lane="${AIRCRAFT_AVAILABILITY_ITEM_TYPE}" data-aircraft-reg="${escapeHtml(aircraftReg)}" data-availability-type="${escapeHtml(availabilityWindow.window_type || "OTHER")}" data-availability-open-ended="${openEnded}">
            <span class="schedule-draft-availability-title" title="${escapeHtml(title)}">${escapeHtml(title)}</span>
            ${item.meta?.openEnded ? "<span class=\"schedule-draft-availability-open-ended\">持续</span>" : ""}
        </div>
    `;
}

function renderItemContent(item) {
    if (item.itemType === AIRCRAFT_AVAILABILITY_ITEM_TYPE || item.meta?.contextType === AIRCRAFT_AVAILABILITY_ITEM_TYPE) {
        return renderAircraftAvailabilityItemContent(item);
    }

    if (item.itemType === "ground" || item.meta?.itemType === "ground") {
        return renderGroundItemContent(item);
    }

    return renderFlightItemContent(item);
}

function parseTimelineDate(value) {
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function normalizeTimelineWindowRange(range) {
    const start = parseTimelineDate(range?.start);
    const end = parseTimelineDate(range?.end);
    if (!start || !end || end.getTime() <= start.getTime()) {
        return null;
    }

    return { start, end };
}

function cloneTimelineWindowRange(range) {
    return range ? { start: new Date(range.start.getTime()), end: new Date(range.end.getTime()) } : null;
}

function getTimelineWindowRangeSignature(range) {
    const normalizedRange = normalizeTimelineWindowRange(range);
    if (!normalizedRange) {
        return null;
    }

    return `${normalizedRange.start.getTime()}:${normalizedRange.end.getTime()}`;
}

function getTimelineWindowRange(timeline, event = null) {
    return normalizeTimelineWindowRange(event)
        || normalizeTimelineWindowRange(typeof timeline?.getWindow === "function" ? timeline.getWindow() : null);
}

function collectTimelineLayoutMetrics(container) {
    if (!container?.querySelectorAll) {
        return {};
    }

    const collectHeightStats = (selector) => {
        const heights = Array.from(container.querySelectorAll(selector)).map((element) => Math.round(element.getBoundingClientRect().height));
        if (!heights.length) {
            return { count: 0, min: 0, max: 0, sample: [] };
        }

        return {
            count: heights.length,
            min: Math.min(...heights),
            max: Math.max(...heights),
            sample: heights.slice(0, 6),
        };
    };

    return {
        labelHeightStats: collectHeightStats(".vis-labelset .vis-label"),
        groupHeightStats: collectHeightStats(".vis-foreground .vis-group"),
        itemHeightStats: collectHeightStats(".vis-item.vis-range"),
    };
}

function debugTimelineDomSnapshot(scope, container, payload = {}, { includeLayoutMetrics = true } = {}) {
    if (!isScheduleDraftTimelineDebugEnabled()) {
        return;
    }

    debugScheduleDraftTimeline(scope, {
        ...payload,
        visLabelCount: container?.querySelectorAll?.(".vis-labelset .vis-label").length || 0,
        visItemCount: container?.querySelectorAll?.(".vis-item").length || 0,
        visRangeCount: container?.querySelectorAll?.(".vis-item.vis-range").length || 0,
        ...(includeLayoutMetrics ? collectTimelineLayoutMetrics(container) : {}),
    });
}

function getMidpointDate(start, end) {
    return new Date(start.getTime() + (end.getTime() - start.getTime()) / 2);
}

function normalizeTimelineItem(item, availableTrackIds) {
    if (!item || !availableTrackIds.has(item.trackId)) {
        return null;
    }

    const start = parseTimelineDate(item.start);
    if (!start) {
        return null;
    }

    const itemType = item.itemType || item.meta?.itemType || "flight";
    const isBackgroundItem = item.type === "background" || itemType === "curfew" || itemType === "background";
    let end = parseTimelineDate(item.end);
    if (isBackgroundItem && (!end || end.getTime() <= start.getTime())) {
        return null;
    }
    if (!end || end.getTime() <= start.getTime()) {
        end = new Date(start.getTime() + DEFAULT_ITEM_DURATION_MS);
    }

    if (isBackgroundItem) {
        const curfewOperation = item.meta?.curfew?.tone || item.meta?.curfew?.operation || "departure";
        const annotationCommonData = {
            group: item.trackId,
            subgroup: TIMELINE_LANES.ground.subgroup,
            timelineLane: TIMELINE_LANES.ground.id,
            "timeline-lane": TIMELINE_LANES.ground.id,
            timelineLaneOrder: TIMELINE_LANES.ground.order,
            timelineItemType: itemType,
            curfewOperation,
            "curfew-operation": curfewOperation,
            selectable: false,
        };

        return [{
            id: item.id,
            group: item.trackId,
            start,
            end,
            type: "background",
            timelineItemType: itemType,
            curfewOperation,
            "curfew-operation": curfewOperation,
            curfewStartLabel: item.meta?.curfew?.edgeStartLabel || item.meta?.curfew?.windowStartLabel || "",
            "curfew-start-label": item.meta?.curfew?.edgeStartLabel || item.meta?.curfew?.windowStartLabel || "",
            curfewEndLabel: item.meta?.curfew?.edgeEndLabel || item.meta?.curfew?.windowEndLabel || "",
            "curfew-end-label": item.meta?.curfew?.edgeEndLabel || item.meta?.curfew?.windowEndLabel || "",
            selectable: false,
            content: renderCurfewBackgroundContent(),
            className: ["schedule-draft-timeline-background-item", sanitizeTimelineClassName(item.className)].filter(Boolean).join(" "),
        }, {
            id: `${item.id}:start`,
            ...annotationCommonData,
            start,
            type: "point",
            curfewAnnotationRole: "start-time",
            content: renderCurfewEdgeTimeContent(item, "start"),
            className: `schedule-draft-timeline-curfew-annotation schedule-draft-timeline-curfew-edge-annotation schedule-draft-timeline-curfew-edge-start schedule-draft-timeline-curfew-annotation-${curfewOperation}`,
        }, {
            id: `${item.id}:label`,
            ...annotationCommonData,
            start: getMidpointDate(start, end),
            type: "point",
            curfewAnnotationRole: "label",
            curfewLabelFull: item.meta?.curfew?.displayLabel || "起飞宵禁",
            "curfew-label-full": item.meta?.curfew?.displayLabel || "起飞宵禁",
            curfewLabelCompact: item.meta?.curfew?.compactLabel || item.meta?.curfew?.displayLabel || "起飞宵禁",
            "curfew-label-compact": item.meta?.curfew?.compactLabel || item.meta?.curfew?.displayLabel || "起飞宵禁",
            content: renderCurfewLabelContent(item),
            className: `schedule-draft-timeline-curfew-annotation schedule-draft-timeline-curfew-label-annotation schedule-draft-timeline-curfew-annotation-${curfewOperation}`,
        }, {
            id: `${item.id}:end`,
            ...annotationCommonData,
            start: end,
            type: "point",
            curfewAnnotationRole: "end-time",
            content: renderCurfewEdgeTimeContent(item, "end"),
            className: `schedule-draft-timeline-curfew-annotation schedule-draft-timeline-curfew-edge-annotation schedule-draft-timeline-curfew-edge-end schedule-draft-timeline-curfew-annotation-${curfewOperation}`,
        }];
    }

    const isGroundItem = itemType === "ground";
    const isAircraftAvailabilityItem = itemType === AIRCRAFT_AVAILABILITY_ITEM_TYPE || item.meta?.contextType === AIRCRAFT_AVAILABILITY_ITEM_TYPE;
    if (isAircraftAvailabilityItem) {
        const lane = TIMELINE_LANES.availability;
        const windowTone = item.meta?.windowTone || "other";
        return {
            id: item.id,
            group: item.trackId,
            subgroup: lane.subgroup,
            timelineLane: lane.id,
            "timeline-lane": lane.id,
            timelineLaneOrder: lane.order,
            timelineItemType: AIRCRAFT_AVAILABILITY_ITEM_TYPE,
            contextType: AIRCRAFT_AVAILABILITY_ITEM_TYPE,
            aircraftReg: item.meta?.aircraftReg || item.meta?.availabilityWindow?.aircraft_reg || null,
            "aircraft-reg": item.meta?.aircraftReg || item.meta?.availabilityWindow?.aircraft_reg || null,
            availabilityType: item.meta?.availabilityWindow?.window_type || null,
            "availability-type": item.meta?.availabilityWindow?.window_type || null,
            availabilityDisplayLabel: item.meta?.fullDisplayLabel || item.fullDisplayLabel || item.title || null,
            "availability-display-label": item.meta?.fullDisplayLabel || item.fullDisplayLabel || item.title || null,
            start,
            end,
            type: "range",
            selectable: item.selectable !== false,
            content: renderAircraftAvailabilityItemContent(item),
            className: [
                "schedule-draft-timeline-item",
                lane.className,
                "schedule-draft-timeline-context-item",
                "schedule-draft-timeline-aircraft-availability-item",
                `schedule-draft-timeline-aircraft-availability-${windowTone}`,
                sanitizeTimelineClassName(item.className),
            ].filter(Boolean).join(" "),
        };
    }

    const lane = isGroundItem ? TIMELINE_LANES.ground : TIMELINE_LANES.flight;
    const ground = item.meta?.ground || {};
    const groundDisplayMode = normalizeGroundDisplayMode(ground.displayMode, ground);
    const groundDisplayLabel = ground.displayLabel || ground.airportCode || (ground.isContinuous ? "" : `${ground.arrivalAirport || "--"}->${ground.departureAirport || "--"}`);
    const groundMarkerRole = normalizeGroundMarkerRole(ground);
    const groundTimeStatusClassName = ground.groundTimeStatus ? `schedule-draft-timeline-ground-time-${ground.groundTimeStatus}` : "";
    let groundContinuityClassName = "";
    if (isGroundItem && ground.isContinuous === true) {
        groundContinuityClassName = "schedule-draft-timeline-ground-continuous";
    } else if (isGroundItem && ground.isContinuous === false) {
        groundContinuityClassName = "schedule-draft-timeline-ground-discontinuous";
    }

    if (isGroundItem) {
        const commonClassNames = [
            lane.className,
            "schedule-draft-timeline-ground",
            groundContinuityClassName,
            `schedule-draft-timeline-ground-mode-${groundDisplayMode}`,
            groundTimeStatusClassName,
            sanitizeTimelineClassName(item.className, { isGroundItem }),
        ].filter(Boolean).join(" ");
        const groundStyle = item.style || null;
        const commonData = {
            group: item.trackId,
            subgroup: lane.subgroup,
            timelineLane: lane.id,
            "timeline-lane": lane.id,
            timelineLaneOrder: lane.order,
            timelineItemType: itemType,
            groundDisplayMode,
            "ground-display-mode": groundDisplayMode,
            groundDisplayLabel,
            "ground-display-label": groundDisplayLabel,
            groundMarkerRole,
            "ground-marker-role": groundMarkerRole,
            groundTimeStatus: ground.groundTimeStatus || null,
            "ground-time-status": ground.groundTimeStatus || null,
            selectable: item.selectable !== false,
        };
        const markerId = `${item.id}:marker`;

        return [
            {
                id: item.id,
                ...commonData,
                start,
                end,
                type: "range",
                groundMarkerItemId: markerId,
                "ground-marker-item-id": markerId,
                content: renderGroundSpanContent(item),
                className: `schedule-draft-timeline-ground-span-item ${commonClassNames}`,
                ...(groundStyle ? { style: groundStyle } : {}),
            },
            {
                id: markerId,
                ...commonData,
                start: getMidpointDate(start, end),
                type: "point",
                groundRangeItemId: item.id,
                "ground-range-item-id": item.id,
                content: renderGroundItemContent(item),
                className: `schedule-draft-timeline-ground-marker-item ${commonClassNames}`,
                ...(groundStyle ? { style: groundStyle } : {}),
            },
        ];
    }

    const baseClassName = isGroundItem
        ? "schedule-draft-timeline-ground-item"
        : "schedule-draft-timeline-item schedule-draft-timeline-item-flight";
    const variantClassName = isGroundItem ? "schedule-draft-timeline-ground" : `schedule-draft-timeline-item-${item.variant}`;
    const className = [
        baseClassName,
        lane.className,
        variantClassName,
        isGroundItem ? "schedule-draft-timeline-ground-centered" : "",
        groundContinuityClassName,
        item.diffStatus && !isGroundItem ? `schedule-draft-timeline-item-diff-${item.diffStatus}` : "",
        isGroundItem ? `schedule-draft-timeline-ground-mode-${groundDisplayMode}` : "",
        sanitizeTimelineClassName(item.className, { isGroundItem }),
    ].filter(Boolean).join(" ");

    return {
        id: item.id,
        group: item.trackId,
        subgroup: lane.subgroup,
        timelineLane: lane.id,
        "timeline-lane": lane.id,
        timelineLaneOrder: lane.order,
        start,
        end,
        type: "range",
        timelineItemType: itemType,
        groundDisplayMode,
        "ground-display-mode": groundDisplayMode,
        groundDisplayLabel,
        "ground-display-label": groundDisplayLabel,
        groundMarkerRole,
        "ground-marker-role": groundMarkerRole,
        content: renderItemContent(item),
        className,
    };
}

function calculateFlightTitleFontSize(width, title) {
    if (!Number.isFinite(width)) {
        return FLIGHT_TITLE_DEFAULT_FONT_SIZE;
    }

    const text = String(title || "").trim();
    if (!text) {
        return FLIGHT_TITLE_DEFAULT_FONT_SIZE;
    }

    const availableWidth = Math.max(0, width - FLIGHT_TITLE_HORIZONTAL_PADDING);
    const expectedTitleWidth = Math.max(1, text.length * FLIGHT_TITLE_CHAR_WIDTH);
    if (availableWidth >= expectedTitleWidth) {
        return FLIGHT_TITLE_DEFAULT_FONT_SIZE;
    }

    const scaledSize = (availableWidth / expectedTitleWidth) * FLIGHT_TITLE_DEFAULT_FONT_SIZE;
    return Math.max(FLIGHT_TITLE_MIN_FONT_SIZE, Math.min(FLIGHT_TITLE_DEFAULT_FONT_SIZE, Math.round(scaledSize * 10) / 10));
}

function calculateFlightTimeFontSize(titleFontSize) {
    return Math.max(
        FLIGHT_TIME_MIN_FONT_SIZE,
        Math.min(FLIGHT_TIME_DEFAULT_FONT_SIZE, Math.round((titleFontSize - 1) * 10) / 10),
    );
}

function estimateTextWidth(value, charWidth) {
    const text = String(value || "").trim();
    return text ? text.length * charWidth : 0;
}

function getMeasuredOrEstimatedTextWidth(element, value, charWidth) {
    const rectWidth = toFiniteNumber(element?.getBoundingClientRect?.().width);
    const scrollWidth = toFiniteNumber(element?.scrollWidth);
    const measuredWidth = Math.max(rectWidth || 0, scrollWidth || 0);
    return measuredWidth > 0 ? measuredWidth : estimateTextWidth(value, charWidth);
}

function calculateFlightTitleCenterReserveWidth(width, title, fontSize) {
    const text = String(title || "").trim();
    const scaledTitleWidth = estimateTextWidth(text, FLIGHT_TITLE_CHAR_WIDTH) * (fontSize / FLIGHT_TITLE_DEFAULT_FONT_SIZE);
    const availableWidth = Number.isFinite(width) ? Math.max(0, width - FLIGHT_TITLE_HORIZONTAL_PADDING) : scaledTitleWidth;
    return Math.min(
        availableWidth,
        Math.max(FLIGHT_MIN_CENTER_RESERVE_WIDTH, scaledTitleWidth + FLIGHT_TITLE_CENTER_PADDING),
    );
}

function resolveFlightDisplayMode({ width, title, departureLabel, arrivalLabel, departureElement, arrivalElement, titleFontSize, timeFontSize }) {
    if (!Number.isFinite(width)) {
        return { mode: "number-only", centerReserveWidth: FLIGHT_MIN_CENTER_RESERVE_WIDTH, timeMaxWidth: 0 };
    }

    const contentWidth = Math.max(0, width - FLIGHT_TITLE_HORIZONTAL_PADDING);
    const centerReserveWidth = calculateFlightTitleCenterReserveWidth(width, title, titleFontSize);
    const timeMaxWidth = Math.max(0, (contentWidth - centerReserveWidth) / 2 - FLIGHT_TIME_CENTER_GAP);
    const hasDepartureLabel = Boolean(String(departureLabel || "").trim());
    const hasArrivalLabel = Boolean(String(arrivalLabel || "").trim());
    const timeCharWidth = FLIGHT_TIME_CHAR_WIDTH * (timeFontSize / FLIGHT_TIME_DEFAULT_FONT_SIZE);
    const departureWidth = getMeasuredOrEstimatedTextWidth(departureElement, departureLabel, timeCharWidth);
    const arrivalWidth = getMeasuredOrEstimatedTextWidth(arrivalElement, arrivalLabel, timeCharWidth);
    const canShowDeparture = hasDepartureLabel && timeMaxWidth >= departureWidth;
    const canShowArrival = hasArrivalLabel && timeMaxWidth >= arrivalWidth;
    if (canShowDeparture && canShowArrival) {
        return { mode: "time-range", centerReserveWidth, timeMaxWidth };
    }
    return { mode: "number-only", centerReserveWidth, timeMaxWidth };
}

function applyFlightDisplayMode(element, mode) {
    const showDeparture = mode === "time-range";
    const showArrival = mode === "time-range";

    element.classList.toggle("schedule-draft-timeline-flight-mode-number", mode === "number-only");
    element.classList.toggle("schedule-draft-timeline-flight-mode-departure", false);
    element.classList.toggle("schedule-draft-timeline-flight-mode-full", mode === "time-range");
    element.classList.toggle("schedule-draft-timeline-flight-number-only", mode === "number-only");
    element.classList.toggle("schedule-draft-timeline-flight-show-departure", showDeparture);
    element.classList.toggle("schedule-draft-timeline-flight-show-times", showArrival);
    element.dataset.flightDisplayMode = mode;
    element.setAttribute("data-flight-display-mode", mode);
}

function syncFlightItemDisplayModes(container) {
    if (!container?.querySelectorAll) {
        return;
    }

    Array.from(container.querySelectorAll(".vis-item.schedule-draft-timeline-item-flight")).forEach((element) => {
        const rect = element.getBoundingClientRect?.();
        const width = toFiniteNumber(rect?.width);
        const contentElement = element.querySelector?.(".schedule-draft-flight-lane-content");
        const titleElement = element.querySelector?.(".schedule-draft-flight-number");
        const departureElement = element.querySelector?.(".schedule-draft-flight-time-departure");
        const arrivalElement = element.querySelector?.(".schedule-draft-flight-time-arrival");
        const title = titleElement?.textContent || contentElement?.dataset?.flightTitle || "";
        const departureLabel = contentElement?.dataset?.flightDeparture || departureElement?.textContent || "";
        const arrivalLabel = contentElement?.dataset?.flightArrival || arrivalElement?.textContent || "";
        const titleFontSize = calculateFlightTitleFontSize(width, title);
        const timeFontSize = calculateFlightTimeFontSize(titleFontSize);
        const display = resolveFlightDisplayMode({
            width,
            title,
            departureLabel,
            arrivalLabel,
            departureElement,
            arrivalElement,
            titleFontSize,
            timeFontSize,
        });

        applyFlightDisplayMode(element, display.mode);
        if (contentElement) {
            contentElement.style.setProperty("--schedule-draft-flight-center-reserve-width", `${Math.round(display.centerReserveWidth * 10) / 10}px`);
            contentElement.style.setProperty("--schedule-draft-flight-time-max-width", `${Math.round(display.timeMaxWidth * 10) / 10}px`);
            contentElement.style.setProperty("--schedule-draft-flight-time-font-size", `${timeFontSize}px`);
        }

        if (titleElement) {
            titleElement.style.setProperty("--schedule-draft-flight-title-font-size", `${titleFontSize}px`);
        }
    });
}

function syncGroundMarkerDisplayModes(container) {
    if (!container?.querySelectorAll) {
        return;
    }

    const markerUpdates = Array.from(container.querySelectorAll(".vis-point.schedule-draft-timeline-ground-marker-item")).map((element) => {
        const displayLabel = element.dataset.groundDisplayLabel || element.getAttribute("data-ground-display-label") || "";
        const markerRole = element.dataset.groundMarkerRole || element.getAttribute("data-ground-marker-role") || "turnaround";
        return {
            element,
            displayLabel,
            markerRole,
            mode: "horizontal",
        };
    });

    markerUpdates.forEach(({ element, displayLabel, markerRole, mode }) => {
        applyGroundMarkerDisplayMode(element, mode, { displayLabel, markerRole });
    });
}

function getTimelineElementItemId(element) {
    const contentElement = element?.querySelector?.("[data-timeline-item-id]");
    return element?.["vis-item"]?.id
        ?? element?.dataset?.id
        ?? element?.getAttribute?.("data-id")
        ?? contentElement?.dataset?.timelineItemId
        ?? contentElement?.getAttribute?.("data-timeline-item-id")
        ?? null;
}

function buildTimelineItemElementMap(container) {
    const itemElementById = new Map();
    if (!container?.querySelectorAll) {
        return itemElementById;
    }

    Array.from(container.querySelectorAll(".vis-item")).forEach((element) => {
        const itemId = getTimelineElementItemId(element);
        if (itemId != null && !itemElementById.has(String(itemId))) {
            itemElementById.set(String(itemId), element);
        }
    });
    return itemElementById;
}

function syncCurfewBackgroundDisplayModes(container) {
    if (!container?.querySelectorAll) {
        return;
    }

    const itemElementById = buildTimelineItemElementMap(container);
    const findItemElement = (itemId) => itemElementById.get(String(itemId)) || null;

    Array.from(container.querySelectorAll(".vis-item.schedule-draft-timeline-curfew-background")).forEach((element) => {
        const itemId = element.dataset.id || element.getAttribute("data-id");
        const width = element.getBoundingClientRect?.().width;
        const isNarrow = Number.isFinite(width) && width < 96;
        const isCompact = Number.isFinite(width) && width < 58;

        [element, findItemElement(`${itemId}:label`), findItemElement(`${itemId}:start`), findItemElement(`${itemId}:end`)].filter(Boolean).forEach((target) => {
            target.classList.toggle("schedule-draft-timeline-curfew-narrow", isNarrow);
            target.classList.toggle("schedule-draft-timeline-curfew-compact", isCompact);
        });

        const labelItemElement = findItemElement(`${itemId}:label`);
        const labelElement = labelItemElement?.querySelector?.(".schedule-draft-curfew-label-text");
        if (labelElement) {
            labelElement.textContent = isNarrow ? labelItemElement.dataset.curfewLabelCompact || labelElement.textContent : labelItemElement.dataset.curfewLabelFull || labelElement.textContent;
        }
    });
}

function syncResponsiveTimelineDecorations(container) {
    syncFlightItemDisplayModes(container);
    syncGroundMarkerDisplayModes(container);
    syncCurfewBackgroundDisplayModes(container);
}

function toPositiveNumber(value, fallback) {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : fallback;
}

function toFiniteNumber(value) {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
}

function getViewportPointFromPointerEvent(event) {
    const source = event?.srcEvent || event?.event?.srcEvent || event?.event || event;
    const x = toFiniteNumber(source?.clientX ?? source?.center?.x);
    const y = toFiniteNumber(source?.clientY ?? source?.center?.y);
    return x === null || y === null ? null : { x, y };
}

function normalizeViewportRect(rect) {
    const left = toFiniteNumber(rect?.left ?? rect?.x);
    const top = toFiniteNumber(rect?.top ?? rect?.y);
    const width = toFiniteNumber(rect?.width);
    const height = toFiniteNumber(rect?.height);
    const right = toFiniteNumber(rect?.right) ?? (left !== null && width !== null ? left + width : null);
    const bottom = toFiniteNumber(rect?.bottom) ?? (top !== null && height !== null ? top + height : null);

    if (left === null || top === null || right === null || bottom === null) {
        return null;
    }

    return {
        left,
        top,
        right,
        bottom,
        width: width ?? Math.max(0, right - left),
        height: height ?? Math.max(0, bottom - top),
    };
}

function findTimelineItemElement(container, itemId) {
    if (!container?.querySelectorAll || itemId == null) {
        return null;
    }

    const targetId = String(itemId);
    return Array.from(container.querySelectorAll(".vis-item")).find((candidate) => {
        const candidateId = getTimelineElementItemId(candidate);
        return String(candidateId) === targetId;
    }) || null;
}

function getElementViewportRect(element) {
    return normalizeViewportRect(element?.getBoundingClientRect?.());
}

function getViewportPointFromAnchorRect(anchorRect) {
    if (!anchorRect) {
        return null;
    }
    return {
        x: anchorRect.left + anchorRect.width / 2,
        y: anchorRect.top + anchorRect.height / 2,
    };
}

function buildTimelineHoverContext({ event, itemId, container, trackRowHeight } = {}) {
    const anchorElement = findTimelineItemElement(container, itemId);
    const anchorRect = getElementViewportRect(anchorElement);
    return {
        viewportPoint: getViewportPointFromAnchorRect(anchorRect) || getViewportPointFromPointerEvent(event),
        anchorRect,
        anchorElement: anchorElement || null,
        trackRowHeight,
    };
}

function getTimelineHoverSignature(itemId, hoverContext) {
    const point = hoverContext?.viewportPoint;
    const anchor = hoverContext?.anchorRect;
    return [
        itemId ?? "",
        anchor?.left ?? "",
        anchor?.top ?? "",
        anchor?.right ?? "",
        anchor?.bottom ?? "",
        point?.x ?? "",
        point?.y ?? "",
        hoverContext?.trackRowHeight ?? "",
    ].join("|");
}

function calculateTimelineHeight(trackCount, layout = {}) {
    const safeTrackCount = Math.max(Number(trackCount) || 0, 1);
    const minHeight = toPositiveNumber(layout.minHeight, TIMELINE_MIN_HEIGHT);
    const axisHeight = toPositiveNumber(layout.axisHeight, TIMELINE_AXIS_HEIGHT);
    const trackRowHeight = toPositiveNumber(layout.trackRowHeight, TIMELINE_TRACK_ROW_HEIGHT);
    return `${Math.max(minHeight, axisHeight + safeTrackCount * trackRowHeight)}px`;
}

function createTimelineOptions(timeWindow, applyWindow, trackCount = 0, layout = {}) {
    const options = {
        // The ScheduleDraft workspace is LTR. Declaring it prevents vis-timeline
        // from hiding the root node while it auto-detects direction during the
        // first draw, which can otherwise leave mode-mounted canvases invisible.
        rtl: false,
        // vis-timeline 默认按设备本地时区渲染时间轴刻度与日期标签；
        // ScheduleDraft 全链路是 UTC 语义（契约 11_time_contract.md §4.5），刻度必须固定为 UTC。
        moment: (date) => moment(date).utc(),
        orientation: "top",
        stack: false,
        stackSubgroups: true,
        horizontalScroll: true,
        verticalScroll: false,
        zoomKey: "ctrlKey",
        showCurrentTime: true,
        editable: false,
        height: calculateTimelineHeight(trackCount, layout),
        margin: { item: TIMELINE_ITEM_MARGIN, axis: TIMELINE_AXIS_MARGIN },
        groupHeightMode: "fixed",
        xss: {
            disabled: false,
            filterOptions: TIMELINE_CONTENT_XSS_FILTER_OPTIONS,
        },
        dataAttributes: [
            "id",
            "group",
            "timelineItemType",
            "timelineLane",
            "timeline-lane",
            "groundDisplayMode",
            "ground-display-mode",
            "groundDisplayLabel",
            "ground-display-label",
            "groundMarkerRole",
            "ground-marker-role",
            "groundRangeItemId",
            "ground-range-item-id",
            "groundMarkerItemId",
            "ground-marker-item-id",
            "curfewOperation",
            "curfew-operation",
            "curfewStartLabel",
            "curfew-start-label",
            "curfewEndLabel",
            "curfew-end-label",
            "curfewLabelFull",
            "curfew-label-full",
            "curfewLabelCompact",
            "curfew-label-compact",
            "contextType",
            "aircraftReg",
            "aircraft-reg",
            "availabilityType",
            "availability-type",
        ],
    };

    if (applyWindow && timeWindow?.start && timeWindow?.end) {
        options.start = new Date(timeWindow.start);
        options.end = new Date(timeWindow.end);
    }

    return options;
}

function getTimelineOptionsSignature(options = {}) {
    return [
        options.height || "",
        options.start instanceof Date ? options.start.getTime() : "",
        options.end instanceof Date ? options.end.getTime() : "",
    ].join("|");
}

export function createTimelineAdapter({ container, onSelectItem, onLayoutChange, onHoverItem, onTrackAction, onRangeChange, itemPreviewTrigger = ITEM_PREVIEW_TRIGGER_HOVER }) {
    let timeline = null;
    let isInitialized = false;
    let itemLookup = new Map();
    let trackLookup = new Map();
    let lastRenderedItemCount = 0;
    let modelTimeWindow = null;
    let currentTimeWindow = null;
    let decorationSyncFrame = null;
    let pendingDecorationSyncSources = new Set();
    let shouldNotifyDecorationLayout = false;
    let layoutRefreshFrame = null;
    let pendingLayoutRefresh = null;
    let currentHoveredItemId = null;
    let lastHoverSignature = null;
    let currentTrackRowHeight = TIMELINE_TRACK_ROW_HEIGHT;
    let suppressedRangeChangeDepth = 0;
    let pendingProgrammaticRange = null;
    let rangeChangeFrame = null;
    let pendingRangeChangeReport = null;
    let lastTimelineOptionsSignature = null;
    const previewTrigger = itemPreviewTrigger === ITEM_PREVIEW_TRIGGER_CLICK
        ? ITEM_PREVIEW_TRIGGER_CLICK
        : itemPreviewTrigger === ITEM_PREVIEW_TRIGGER_NONE
          ? ITEM_PREVIEW_TRIGGER_NONE
          : ITEM_PREVIEW_TRIGGER_HOVER;
    const items = new DataSet([]);
    const groups = new DataSet([]);

    const clearPendingRangeChangeReport = () => {
        if (rangeChangeFrame !== null) {
            cancelAnimationFrame(rangeChangeFrame);
            rangeChangeFrame = null;
        }
        pendingRangeChangeReport = null;
    };

    const emitRangeChange = (source, range) => {
        onRangeChange?.({
            source,
            range: cloneTimelineWindowRange(range),
        });
    };

    const scheduleRangeChangeReport = (source, range) => {
        pendingRangeChangeReport = { source, range: cloneTimelineWindowRange(range) };
        if (rangeChangeFrame !== null) {
            return;
        }

        rangeChangeFrame = requestAnimationFrame(() => {
            rangeChangeFrame = null;
            const report = pendingRangeChangeReport;
            pendingRangeChangeReport = null;
            if (report?.range) {
                emitRangeChange(report.source, report.range);
            }
        });
    };

    const rememberProgrammaticRange = (range, meta = {}) => {
        const normalizedRange = normalizeTimelineWindowRange(range);
        const signature = getTimelineWindowRangeSignature(normalizedRange);
        if (!normalizedRange || !signature) {
            pendingProgrammaticRange = null;
            return;
        }

        pendingProgrammaticRange = {
            signature,
            sourceId: meta.sourceId || null,
            source: meta.source || null,
            expiresAt: Date.now() + 750,
        };
    };

    const shouldSuppressProgrammaticRange = (eventName, range) => {
        if (!pendingProgrammaticRange) {
            return false;
        }

        if (Date.now() > pendingProgrammaticRange.expiresAt) {
            pendingProgrammaticRange = null;
            return false;
        }

        if (getTimelineWindowRangeSignature(range) !== pendingProgrammaticRange.signature) {
            return false;
        }

        if (eventName === "rangechanged") {
            pendingProgrammaticRange = null;
        }
        return true;
    };

    const withSuppressedRangeChange = (operation) => {
        suppressedRangeChangeDepth += 1;
        try {
            return operation();
        } finally {
            suppressedRangeChangeDepth -= 1;
        }
    };

    const buildWindowResult = (range, changed) => ({
        range: cloneTimelineWindowRange(range),
        changed,
    });

    const setTimelineWindow = (range, { suppressRangeChange = true, animation = false, sourceId = null, source = null, force = false } = {}) => {
        if (!timeline) {
            return null;
        }

        const normalizedRange = normalizeTimelineWindowRange(range);
        if (!normalizedRange) {
            return null;
        }

        const nextSignature = getTimelineWindowRangeSignature(normalizedRange);
        const currentSignature = getTimelineWindowRangeSignature(currentTimeWindow || getTimelineWindowRange(timeline));
        if (!force && nextSignature && currentSignature === nextSignature) {
            currentTimeWindow = cloneTimelineWindowRange(normalizedRange);
            return buildWindowResult(normalizedRange, false);
        }

        if (suppressRangeChange) {
            rememberProgrammaticRange(normalizedRange, { sourceId, source });
        }

        const applyWindow = () => {
            timeline.setWindow(normalizedRange.start, normalizedRange.end, { animation });
        };
        if (suppressRangeChange) {
            withSuppressedRangeChange(applyWindow);
        } else {
            applyWindow();
        }
        currentTimeWindow = cloneTimelineWindowRange(normalizedRange);
        return buildWindowResult(normalizedRange, true);
    };

    const reportRangeChange = (source, event) => {
        const range = getTimelineWindowRange(timeline, event);
        if (!range) {
            return false;
        }

        currentTimeWindow = cloneTimelineWindowRange(range);
        if (suppressedRangeChangeDepth > 0) {
            return false;
        }
        if (shouldSuppressProgrammaticRange(source, range)) {
            return false;
        }

        if (source === "rangechange") {
            scheduleRangeChangeReport(source, range);
            return true;
        }

        if (source === "rangechanged") {
            clearPendingRangeChangeReport();
        }
        emitRangeChange(source, range);
        return true;
    };

    const clearHoverItem = (itemId = currentHoveredItemId) => {
        if (itemId && currentHoveredItemId && itemId !== currentHoveredItemId) {
            return;
        }
        if (!currentHoveredItemId && lastHoverSignature === null) {
            return;
        }
        currentHoveredItemId = null;
        lastHoverSignature = null;
        onHoverItem?.(null);
    };

    const getLogicalItemId = (itemId) => itemLookup.get(itemId)?.id ?? itemId;

    const reportHoverItem = (itemId, event) => {
        const hoveredItem = itemLookup.get(itemId) || null;
        if (!hoveredItem || hoveredItem.selectable === false) {
            clearHoverItem();
            return;
        }
        const hoverContext = buildTimelineHoverContext({
            event,
            itemId,
            container,
            trackRowHeight: currentTrackRowHeight,
        });
        const hoverSignature = getTimelineHoverSignature(itemId, hoverContext);
        if (hoverSignature === lastHoverSignature) {
            return;
        }
        currentHoveredItemId = itemId;
        lastHoverSignature = hoverSignature;
        onHoverItem?.(hoveredItem, hoverContext);
    };

    const toggleClickHoverItem = (itemId, event) => {
        if (currentHoveredItemId && getLogicalItemId(currentHoveredItemId) === getLogicalItemId(itemId)) {
            clearHoverItem();
            return;
        }

        reportHoverItem(itemId, event);
    };

    const refreshCurrentHoverAnchor = () => {
        if (currentHoveredItemId) {
            reportHoverItem(currentHoveredItemId, null);
        }
    };

    const handleContainerMouseLeave = () => {
        if (previewTrigger !== ITEM_PREVIEW_TRIGGER_HOVER) {
            return;
        }
        clearHoverItem();
    };

    const handleContainerClick = (event) => {
        const actionButton = event.target?.closest?.("[data-timeline-track-action]");
        if (!actionButton || !container?.contains?.(actionButton)) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        const track = trackLookup.get(actionButton.dataset.timelineTrackId) || null;
        const action = track?.action || null;
        if (!action) {
            return;
        }
        onTrackAction?.(action, track);
    };

    const emitLayoutChange = (source, sources = [source]) => {
        onLayoutChange?.({ source: source || "layout", sources: sources.filter(Boolean) });
    };

    const scheduleTimelineDecorationSync = (source = null, { notifyLayout = false } = {}) => {
        if (source) {
            pendingDecorationSyncSources.add(source);
        }
        shouldNotifyDecorationLayout = shouldNotifyDecorationLayout || notifyLayout;
        if (decorationSyncFrame !== null) {
            return;
        }

        decorationSyncFrame = requestAnimationFrame(() => {
            const sources = Array.from(pendingDecorationSyncSources);
            const notify = shouldNotifyDecorationLayout;
            decorationSyncFrame = null;
            pendingDecorationSyncSources = new Set();
            shouldNotifyDecorationLayout = false;
            syncResponsiveTimelineDecorations(container);
            refreshCurrentHoverAnchor();
            if (notify) {
                emitLayoutChange(sources[0] || "timeline-decorations", sources);
            }
        });
    };

    const cancelTimelineDecorationSync = () => {
        if (decorationSyncFrame === null) {
            return;
        }

        cancelAnimationFrame(decorationSyncFrame);
        decorationSyncFrame = null;
        pendingDecorationSyncSources = new Set();
        shouldNotifyDecorationLayout = false;
    };

    const cancelLayoutRefresh = () => {
        if (layoutRefreshFrame === null) {
            return;
        }

        cancelAnimationFrame(layoutRefreshFrame);
        layoutRefreshFrame = null;
        pendingLayoutRefresh = null;
    };

    const getPrimaryLayoutRefreshSource = (sources = []) => {
        if (sources.includes("render")) {
            return "render";
        }
        return sources[0] || "refresh-layout";
    };

    const scheduleLayoutRefresh = (source = "refresh-layout", { restoreWindow = false, debugScope = null } = {}) => {
        if (!timeline) {
            return;
        }

        const normalizedSource = source || "refresh-layout";
        pendingLayoutRefresh = pendingLayoutRefresh || {
            sources: [],
            restoreWindow: false,
            debugScope: null,
        };
        if (!pendingLayoutRefresh.sources.includes(normalizedSource)) {
            pendingLayoutRefresh.sources.push(normalizedSource);
        }
        pendingLayoutRefresh.restoreWindow = pendingLayoutRefresh.restoreWindow || restoreWindow;
        pendingLayoutRefresh.debugScope = pendingLayoutRefresh.debugScope || debugScope || (normalizedSource === "render" ? "adapter-render-dom" : "adapter-refresh-layout");

        if (layoutRefreshFrame !== null) {
            return;
        }

        cancelTimelineDecorationSync();
        layoutRefreshFrame = requestAnimationFrame(() => {
            const refresh = pendingLayoutRefresh || { sources: ["refresh-layout"], restoreWindow: false, debugScope: "adapter-refresh-layout" };
            layoutRefreshFrame = null;
            pendingLayoutRefresh = null;
            if (!timeline) {
                return;
            }

            const primarySource = getPrimaryLayoutRefreshSource(refresh.sources);
            timeline.redraw();
            const preservedWindow = currentTimeWindow || modelTimeWindow;
            if (refresh.restoreWindow && preservedWindow) {
                setTimelineWindow(preservedWindow, { suppressRangeChange: true, animation: false });
            } else if (refresh.restoreWindow && lastRenderedItemCount) {
                withSuppressedRangeChange(() => timeline?.fit({ animation: false }));
                currentTimeWindow = getTimelineWindowRange(timeline);
            }
            syncResponsiveTimelineDecorations(container);
            refreshCurrentHoverAnchor();
            emitLayoutChange(primarySource, refresh.sources);

            debugTimelineDomSnapshot(refresh.debugScope, container, {
                source: primarySource,
                sources: refresh.sources,
                hasTimeWindow: Boolean(preservedWindow),
                lastRenderedItemCount,
            });
        });
    };

    const ensureTimeline = () => {
        if (timeline || !container) {
            return;
        }

        timeline = new Timeline(container, items, groups, createTimelineOptions(null, false));
        timeline.on("click", (event) => {
            if (!event.item) {
                if (previewTrigger === ITEM_PREVIEW_TRIGGER_CLICK) {
                    clearHoverItem();
                }
                return;
            }

            const selectedItem = itemLookup.get(event.item) || null;
            if (!selectedItem || selectedItem.selectable === false) {
                if (previewTrigger === ITEM_PREVIEW_TRIGGER_CLICK) {
                    clearHoverItem();
                }
                return;
            }

            if (previewTrigger === ITEM_PREVIEW_TRIGGER_CLICK) {
                toggleClickHoverItem(event.item, event);
                return;
            }

            onSelectItem?.(selectedItem);
        });

        timeline.on("itemover", (event) => {
            if (previewTrigger !== ITEM_PREVIEW_TRIGGER_HOVER) {
                return;
            }
            reportHoverItem(event.item, event);
        });

        timeline.on("itemout", (event) => {
            if (previewTrigger !== ITEM_PREVIEW_TRIGGER_HOVER) {
                return;
            }
            const hoveredItem = itemLookup.get(event.item) || null;
            if (!hoveredItem || hoveredItem.selectable === false) {
                return;
            }
            clearHoverItem(event.item);
        });

        container.addEventListener("mouseleave", handleContainerMouseLeave);
        container.addEventListener("click", handleContainerClick);

        ["rangechange", "rangechanged"].forEach((eventName) => {
            timeline.on(eventName, (event) => {
                const shouldSyncLayout = reportRangeChange(eventName, event);
                if (!shouldSyncLayout) {
                    return;
                }
                if (eventName === "rangechange") {
                    emitLayoutChange(eventName, [eventName]);
                    return;
                }
                scheduleTimelineDecorationSync(eventName, { notifyLayout: true });
            });
        });

        timeline.on("changed", () => {
            scheduleTimelineDecorationSync("changed", { notifyLayout: true });
        });
    };

    return {
        render(model) {
            ensureTimeline();

            if (!timeline) {
                debugScheduleDraftTimeline("adapter-render-skipped", {
                    reason: "timeline-instance-missing",
                    hasContainer: Boolean(container),
                });
                return;
            }

            itemLookup = new Map();
            trackLookup = new Map();
            (model.items || []).forEach((item) => {
                itemLookup.set(item.id, item);
                if (item.itemType === "ground" || item.meta?.itemType === "ground") {
                    itemLookup.set(`${item.id}:marker`, item);
                }
            });
            const modelGroups = orderTimelineGroups(model.tracks, model.contextLanes);
            modelGroups.forEach((track) => {
                if (track?.id) {
                    trackLookup.set(String(track.id), track);
                }
            });
            currentTrackRowHeight = toPositiveNumber(model.layout?.trackRowHeight, TIMELINE_TRACK_ROW_HEIGHT);
            const normalizedGroups = modelGroups.map((track) => ({
                id: track.id,
                content: renderTrackLabel(track),
                className: `schedule-draft-track-group schedule-draft-track-group-${track.kind}`,
                subgroupOrder: "timelineLaneOrder",
            }));
            const availableTrackIds = new Set(normalizedGroups.map((group) => group.id));
            const normalizedItems = (model.items || []).flatMap((item) => normalizeTimelineItem(item, availableTrackIds) || []);

            debugScheduleDraftTimeline("adapter-render-input", {
                trackCount: model?.tracks?.length || 0,
                contextLaneCount: model?.contextLanes?.length || 0,
                itemCount: model?.items?.length || 0,
                normalizedTrackCount: normalizedGroups.length,
                normalizedItemCount: normalizedItems.length,
                timeWindow: model?.timeWindow,
                firstItems: normalizedItems.slice(0, 3).map((item) => ({
                    id: item.id,
                    group: item.group,
                    start: item.start?.toISOString?.() || item.start,
                    end: item.end?.toISOString?.() || item.end,
                })),
            });

            groups.clear();
            groups.add(normalizedGroups);

            items.clear();
            items.add(normalizedItems);

            timeline.setGroups(groups);
            timeline.setItems(items);
            const nextOptions = createTimelineOptions(model.timeWindow, !isInitialized, normalizedGroups.length, model.layout);
            const nextOptionsSignature = getTimelineOptionsSignature(nextOptions);
            if (nextOptionsSignature !== lastTimelineOptionsSignature) {
                timeline.setOptions(nextOptions);
                lastTimelineOptionsSignature = nextOptionsSignature;
            }

            modelTimeWindow = normalizeTimelineWindowRange(model.timeWindow);
            if (modelTimeWindow) {
                setTimelineWindow(modelTimeWindow, { suppressRangeChange: true, animation: false, force: !isInitialized });
            } else if (normalizedItems.length && (!isInitialized || !currentTimeWindow || (!lastRenderedItemCount && normalizedItems.length))) {
                withSuppressedRangeChange(() => timeline.fit({ animation: false }));
                currentTimeWindow = getTimelineWindowRange(timeline);
            }

            isInitialized = true;
            lastRenderedItemCount = normalizedItems.length;
            scheduleLayoutRefresh("render", { debugScope: "adapter-render-dom" });
        },

        clear() {
            if (!timeline) {
                return;
            }

            cancelLayoutRefresh();
            cancelTimelineDecorationSync();
            groups.clear();
            items.clear();
            timeline.setGroups(groups);
            timeline.setItems(items);
            timeline.redraw();
            lastRenderedItemCount = 0;
            modelTimeWindow = null;
            currentTimeWindow = null;
            lastTimelineOptionsSignature = null;
            debugTimelineDomSnapshot("adapter-clear", container, {}, { includeLayoutMetrics: false });
        },

        refreshLayout(source = "refresh-layout") {
            if (!timeline) {
                return;
            }

            scheduleLayoutRefresh(source, { restoreWindow: true, debugScope: "adapter-refresh-layout" });
        },

        zoomIn() {
            if (!timeline) {
                return;
            }

            timeline.zoomIn(0.2);
            scheduleTimelineDecorationSync("zoom-in", { notifyLayout: true });
            reportRangeChange("zoom-in");
        },

        zoomOut() {
            if (!timeline) {
                return;
            }

            timeline.zoomOut(0.2);
            scheduleTimelineDecorationSync("zoom-out", { notifyLayout: true });
            reportRangeChange("zoom-out");
        },

        fit() {
            if (!timeline) {
                return;
            }

            if (modelTimeWindow) {
                const previousSignature = getTimelineWindowRangeSignature(currentTimeWindow);
                const result = setTimelineWindow(modelTimeWindow, { suppressRangeChange: true, animation: false });
                scheduleTimelineDecorationSync("fit", { notifyLayout: true });
                if (result?.changed && previousSignature !== getTimelineWindowRangeSignature(result.range)) {
                    reportRangeChange("fit", modelTimeWindow);
                }
                return;
            }

            timeline.fit();
            scheduleTimelineDecorationSync("fit", { notifyLayout: true });
            reportRangeChange("fit");
        },

        setWindow(range, options = {}) {
            const result = setTimelineWindow(range, {
                suppressRangeChange: true,
                animation: false,
                sourceId: options.sourceId,
                source: options.source,
            });
            if (!result?.range || !result.changed) {
                return;
            }
            if (options.source === "rangechange") {
                emitLayoutChange("set-window", ["set-window", "rangechange"]);
                return;
            }
            scheduleTimelineDecorationSync("set-window", { notifyLayout: true });
        },

        getWindow() {
            return cloneTimelineWindowRange(currentTimeWindow || getTimelineWindowRange(timeline));
        },

        clearPreview() {
            clearHoverItem();
        },

        redraw() {
            timeline?.redraw();
            scheduleTimelineDecorationSync("redraw", { notifyLayout: true });
        },

        destroy() {
            if (!timeline) {
                return;
            }

            timeline.destroy();
            container?.removeEventListener?.("mouseleave", handleContainerMouseLeave);
            container?.removeEventListener?.("click", handleContainerClick);
            cancelTimelineDecorationSync();
            cancelLayoutRefresh();
            clearPendingRangeChangeReport();
            timeline = null;
            isInitialized = false;
            itemLookup = new Map();
            trackLookup = new Map();
            modelTimeWindow = null;
            currentTimeWindow = null;
            lastTimelineOptionsSignature = null;
            pendingProgrammaticRange = null;
            currentHoveredItemId = null;
            lastHoverSignature = null;
        },
    };
}

export default createTimelineAdapter;
